import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Table,
  Card,
  Typography,
  Tag,
  Space,
  DatePicker,
  Button,
  Empty,
  Alert,
  Modal,
  Form,
  Input,
  Descriptions,
  Divider,
  Tabs,
  InputNumber,
  Select,
} from "antd";
import moment from "moment";
import {
  ReloadOutlined,
  ExclamationCircleOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import {
  getProductionHistory,
  getProductionCostTracking,
  getProductionDetails,
  deleteProductionLog,
  declareProductionLotLoss,
} from "./api";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import { message } from "antd";
import { getSelectedProductionTerminology } from "./productionTerminology";
import { useNavigate } from "react-router-dom";
import ProductionExpenseRegularizationModal from "./ProductionExpenseRegularizationModal";
import { useQueryClient } from "@tanstack/react-query";
import useBusinessResource from "../hooks/useBusinessResource";
import { invalidateBusinessResources } from "../utils/businessResourceFreshness";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import ProductionHistoryWorkspace, { productionResourceNotice, useProductionHistoryStyle } from "./ProductionHistoryWorkspace";
import ProductionDetailsModal from "./ProductionDetailsModal";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Helper function for currency formatting
const formatCurrency = (value) => {
  if (value === undefined || value === null || isNaN(parseFloat(value))) {
    return "N/A";
  }
  const numValue = parseFloat(value);
  const roundedValue = Math.round(numValue);
  return `${roundedValue.toLocaleString("fr-FR")} CFA`;
};

const PRODUCTION_KIND_SEMI_FINISHED = "semi_finished";
const getProductionKindLabel = (kind) =>
  kind === PRODUCTION_KIND_SEMI_FINISHED ? "Semi-fini" : "Fini";
const getProductionKindColor = (kind) =>
  kind === PRODUCTION_KIND_SEMI_FINISHED ? "gold" : "blue";
const PRODUCTION_LOSS_TYPE_OPTIONS = [
  { value: "unsold", label: "Invendu" },
  { value: "expired", label: "Perime" },
  { value: "damaged", label: "Endommage" },
  { value: "quality_issue", label: "Non conforme" },
  { value: "malformation", label: "Malformation" },
  { value: "other", label: "Autre" },
];
const PRODUCTION_LOSS_TYPE_LABELS = PRODUCTION_LOSS_TYPE_OPTIONS.reduce(
  (acc, option) => ({
    ...acc,
    [option.value]: option.label,
  }),
  {}
);
const getLossTypeLabel = (value) =>
  PRODUCTION_LOSS_TYPE_LABELS[value] || PRODUCTION_LOSS_TYPE_LABELS.other;
const getLotStatusLabel = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "available") return "Disponible";
  if (normalized === "exhausted") return "Epuise";
  if (normalized === "cancelled") return "Annule";
  return value || "N/A";
};
const getLotStatusColor = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "available") return "green";
  if (normalized === "exhausted") return "orange";
  if (normalized === "cancelled") return "default";
  return "default";
};

const ProductionHistory = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [activeView, setActiveView] = useState("history");
  const [dateRange, setDateRange] = useState(null);
  const [costPagination, setCostPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const { hasPermission, user } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const queryClient = useQueryClient();
  const workspaceStyle = useProductionHistoryStyle();
  const businessScope = { userId: user?.id, companyId: selectedCompany, warehouseId: selectedWarehouse, moduleScope: "production" };
  const contextKey = JSON.stringify([user?.id, selectedCompany, selectedWarehouse]);
  const currentContext = useRef(contextKey);
  currentContext.current = contextKey;
  const previousContext = useRef(contextKey);
  const productionTerminology = getSelectedProductionTerminology(
    user,
    selectedCompany
  );

  // États pour le modal de détails
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [detailsProductionId, setDetailsProductionId] = useState(null);

  // États pour l'annulation (suppression) d'une production
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteForm] = Form.useForm();
  const [lotLossModalVisible, setLotLossModalVisible] = useState(false);
  const [lotLossSubmitting, setLotLossSubmitting] = useState(false);
  const [selectedLotForLoss, setSelectedLotForLoss] = useState(null);
  const [lotLossForm] = Form.useForm();
  const [selectedVariableCost, setSelectedVariableCost] = useState(null);

  // Obtenir le nom du magasin sélectionné
  const selectedWarehouseName = React.useMemo(() => {
    if (!selectedWarehouse || !user?.assigned_warehouses) return null;
    const warehouse = user.assigned_warehouses.find(
      (w) => w.id === selectedWarehouse
    );
    return warehouse ? warehouse.name : `Établissement ID: ${selectedWarehouse}`;
  }, [selectedWarehouse, user]);

  const canDeclareLotLoss =
    hasPermission(
      "Gestion Commerciale.Approvisionnement.Production.Gerer.create"
    ) ||
    hasPermission(
      "Gestion Commerciale.Approvisionnement.Production.Historique.delete"
    );
  const canRegularizeVariableCost = hasPermission(
    "Gestion Commerciale.Depenses.SaisieDepenses.create"
  );
  const canPayVariableCostExpense =
    hasPermission("Gestion Commerciale.Depenses.SaisieDepenses.view") &&
    hasPermission(
      "Gestion Commerciale.Depenses.SaisieDepenses.create_payment"
    );
  const canOpenOutgoingPayment =
    hasPermission(
      "Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.view"
    ) &&
    hasPermission(
      "Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.delete"
    );

  const startDate = dateRange?.[0]?.format("YYYY-MM-DD");
  const endDate = dateRange?.[1]?.format("YYYY-MM-DD");
  const filters = { warehouse_id: selectedWarehouse, company_id: selectedCompany, start_date: startDate, end_date: endDate };
  const canLoad = Boolean(selectedCompany && selectedWarehouse);
  const historyResource = useBusinessResource({
    resource: "production-history", ...businessScope,
    parameters: [pagination.current, pagination.pageSize, startDate || "", endDate || ""],
    enabled: canLoad, active: activeView === "history", networkMode: "always",
    load: (signal) => getProductionHistory(pagination.current, pagination.pageSize, filters, { signal, withMetadata: true }),
  });
  const costsResource = useBusinessResource({
    resource: "production-cost-tracking", ...businessScope,
    parameters: [costPagination.current, costPagination.pageSize, startDate || "", endDate || ""],
    enabled: canLoad, active: activeView === "costs", networkMode: "always",
    load: (signal) => getProductionCostTracking(costPagination.current, costPagination.pageSize, filters, { signal, withMetadata: true }),
  });
  const detailsResource = useBusinessResource({
    resource: "production-history-detail", ...businessScope,
    parameters: [detailsProductionId, "modal"],
    enabled: canLoad && detailsModalVisible && Boolean(detailsProductionId),
    active: detailsModalVisible, networkMode: "always",
    load: (signal) => getProductionDetails(detailsProductionId, {
      company_id: selectedCompany, warehouse_id: selectedWarehouse,
    }, { signal, networkOnly: true }),
  });
  const selectedProductionDetails = detailsModalVisible ? productionResourceNotice(detailsResource).data : null;
  const logs = useMemo(() => (historyResource.data?.logs || []).map((log) => ({
    ...log, key: log.id, created_at: moment(log.created_at).format("DD/MM/YYYY HH:mm"),
  })), [historyResource.data]);
  const costLogs = useMemo(() => (costsResource.data?.logs || []).map((log) => ({
    ...log, key: log.id, created_at: moment(log.created_at).format("DD/MM/YYYY HH:mm"),
  })), [costsResource.data]);
  const total = Number(historyResource.data?.total || 0);
  const loading = historyResource.loading;
  const costTotal = Number(costsResource.data?.total || 0);
  const costLoading = costsResource.loading;
  const costSummary = costsResource.data?.summary || null;
  const costByUnit = costsResource.data?.by_unit || [];
  const costMonthlyTrend = costsResource.data?.monthly_trend || [];
  const loadHistory = () => historyResource.refresh({ force: true });
  const loadCostTracking = () => costsResource.refresh({ force: true });

  useEffect(() => {
    if (previousContext.current !== contextKey) {
      previousContext.current = contextKey;
      setDetailsModalVisible(false);
      setDetailsProductionId(null);
      setDeleteModalVisible(false);
      setLogToDelete(null);
      setSelectedVariableCost(null);
      setSelectedLotForLoss(null);
      setLotLossModalVisible(false);
      setDeleteLoading(false);
      setLotLossSubmitting(false);
      setPagination((previous) => ({ ...previous, current: 1 }));
      setCostPagination((previous) => ({ ...previous, current: 1 }));
    }
  }, [contextKey]);

  const handleTableChange = (newPagination) => setPagination(newPagination);
  const handleCostTableChange = (newPagination) => setCostPagination(newPagination);
  const handleDateChange = (dates) => {
    setDateRange(dates);
    setPagination((previous) => ({ ...previous, current: 1 }));
    setCostPagination((previous) => ({ ...previous, current: 1 }));
  };

  // Fonction pour charger et afficher les détails d'une production
  const loadProductionDetails = async (productionId) => {
    if (currentContext.current !== contextKey) return null;
    if (Number(productionId) !== Number(detailsProductionId)) throw new Error("La production sélectionnée a changé.");
    return detailsResource.refresh({ force: true });
  };

  const handleViewDetails = async (record) => {
    if (record?.__offline) {
      message.info(
        "Cette production est encore en attente de synchronisation. Les details serveur seront disponibles apres sync."
      );
      return;
    }

    if (
      !hasPermission(
        "Gestion Commerciale.Approvisionnement.Production.Historique.details"
      )
    ) {
      message.warning("Vous n'avez pas la permission de voir les détails.");
      return;
    }

    setDetailsProductionId(record.id);
    setDetailsModalVisible(true);
  };

  // Fermer le modal de détails
  const handleCloseDetailsModal = () => {
    setDetailsModalVisible(false);
    setDetailsProductionId(null);
    setSelectedLotForLoss(null);
    setSelectedVariableCost(null);
    setLotLossModalVisible(false);
    lotLossForm.resetFields();
  };

  const handleOpenLotLossModal = (lot) => {
    if (!selectedProductionDetails?.production?.id || !lot) {
      return;
    }

    setSelectedLotForLoss(lot);
    lotLossForm.setFieldsValue({
      quantity: undefined,
      loss_type: "unsold",
      notes: "",
    });
    setLotLossModalVisible(true);
  };

  const handleCloseLotLossModal = (force = false) => {
    if (lotLossSubmitting && !force) return;
    setLotLossModalVisible(false);
    setSelectedLotForLoss(null);
    lotLossForm.resetFields();
  };

  const handleSubmitLotLoss = async (values) => {
    if (!selectedProductionDetails?.production?.id || !selectedLotForLoss) {
      return;
    }

    setLotLossSubmitting(true);
    try {
      await declareProductionLotLoss(
        selectedProductionDetails.production.id,
        selectedLotForLoss.lot_id,
        {
          quantity: values.quantity,
          loss_type: values.loss_type,
          notes: values.notes,
        }
      );

      await invalidateBusinessResources(queryClient, businessScope, "production");
      if (currentContext.current !== contextKey) return;
      message.success("Perte sur lot enregistree avec succes");
      handleCloseLotLossModal(true);
      await loadProductionDetails(selectedProductionDetails.production.id);
    } catch (error) {
      if (currentContext.current !== contextKey) return;
      console.error("Erreur lors de la declaration de perte:", error);
      message.error(
        error?.error ||
          error?.message ||
          "Erreur lors de la declaration de perte sur lot"
      );
    } finally {
      if (currentContext.current === contextKey) setLotLossSubmitting(false);
    }
  };

  const handleOpenVariableCostRegularization = (variableCost) => {
    if (canRegularizeVariableCost) setSelectedVariableCost(variableCost);
  };

  const handleOpenExpensePayment = (expenseId) => {
    if (!canPayVariableCostExpense || !expenseId) return;
    handleCloseDetailsModal();
    navigate(
      `/gestion-charges-admin/saisie?expense_id=${expenseId}&action=pay`
    );
  };

  const handleOpenDeleteModal = (record) => {
    setLogToDelete(record);
    setDeleteModalVisible(true);
    deleteForm.resetFields();
  };

  const handleCloseDeleteModal = (force = false) => {
    if (deleteLoading && !force) return;
    setDeleteModalVisible(false);
    setLogToDelete(null);
    deleteForm.resetFields();
  };

  const confirmDeleteProduction = async (values) => {
    if (!logToDelete) return;

    setDeleteLoading(true);
    try {
      await deleteProductionLog(logToDelete.id, values.reason, {
        company_id: selectedCompany,
        warehouse_id: selectedWarehouse,
      });
      await invalidateBusinessResources(queryClient, businessScope, "production");
      if (currentContext.current !== contextKey) return;
      message.success("Production annulée avec succès");
      handleCloseDeleteModal(true);
    } catch (error) {
      if (currentContext.current !== contextKey) return;
      console.error("Erreur lors de l'annulation de la production:", error);
      const errorMessage =
        (typeof error === "string" && error) ||
        error?.message ||
        error?.error ||
        "Erreur lors de l'annulation de la production";

      if (
        error?.code ===
        "PRODUCTION_EXPENSE_PAYMENT_BLOCKS_CANCELLATION"
      ) {
        Modal.error({
          title: "Paiement à annuler avant d'annuler la production",
          width: 680,
          content: (
            <div>
              <p>{errorMessage}</p>
              <Alert
                type="warning"
                showIcon
                message="Action nécessaire"
                description="Annulez d'abord chaque paiement ci-dessous. Revenez ensuite dans l'historique pour annuler la production."
                style={{ marginBottom: 12 }}
              />
              <Space direction="vertical" style={{ width: "100%" }}>
                {(error.blocking_expenses || []).flatMap((expense) =>
                  (expense.payments || []).map((payment) => (
                    <Card
                      key={`${expense.expense_id}-${payment.payment_id}`}
                      size="small"
                    >
                      <Space
                        style={{
                          width: "100%",
                          justifyContent: "space-between",
                        }}
                        wrap
                      >
                        <span>
                          <Text strong>{expense.label}</Text>{" "}
                          <Tag color="red">
                            {payment.payment_number ||
                              `Paiement #${payment.payment_id}`}
                          </Tag>{" "}
                          {formatCurrency(payment.amount)}
                        </span>
                        {canOpenOutgoingPayment ? (
                          <Button
                            type="primary"
                            danger
                            onClick={() => {
                              Modal.destroyAll();
                              handleCloseDeleteModal(true);
                              navigate(
                                `/gestion-achats/paiements-sortants?payment_id=${payment.payment_id}`
                              );
                            }}
                          >
                            Ouvrir le paiement à annuler
                          </Button>
                        ) : (
                          <Text type="secondary">
                            Permission requise : suppression des paiements
                            sortants.
                          </Text>
                        )}
                      </Space>
                    </Card>
                  ))
                )}
              </Space>
            </div>
          ),
        });
        return;
      }

      if (error && typeof error === "object" && error.details) {
        Modal.error({
          title: "Annulation impossible",
          width: 620,
          content: (
            <div>
              <p style={{ marginBottom: 12 }}>{errorMessage}</p>

              <Descriptions bordered size="small" column={1} style={{ marginBottom: 12 }}>
                {error.details.produit && (
                  <Descriptions.Item label="Produit">{error.details.produit}</Descriptions.Item>
                )}
                {error.details.warehouse_id && (
                  <Descriptions.Item label="Établissement (ID)">
                    {error.details.warehouse_id}
                  </Descriptions.Item>
                )}
                {typeof error.details.stock_actuel !== "undefined" && (
                  <Descriptions.Item label="Stock actuel">
                    {Number(error.details.stock_actuel).toFixed(2)}
                  </Descriptions.Item>
                )}
                {typeof error.details.retrait !== "undefined" && (
                  <Descriptions.Item label="Retrait (annulation)">
                    {Number(error.details.retrait).toFixed(2)}
                  </Descriptions.Item>
                )}
                {typeof error.details.stock_apres_annulation !== "undefined" && (
                  <Descriptions.Item label="Stock après annulation">
                    {Number(error.details.stock_apres_annulation).toFixed(2)}
                  </Descriptions.Item>
                )}
              </Descriptions>

              {Array.isArray(error.que_faire) && error.que_faire.length > 0 && (
                <div>
                  <Divider style={{ margin: "12px 0" }} />
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Que faire</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {error.que_faire.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ),
        });
      } else {
        message.error(errorMessage);
      }
    } finally {
      if (currentContext.current === contextKey) setDeleteLoading(false);
    }
  };

  // Rendu conditionnel si aucun magasin n'est sélectionné
  if (!canLoad && !loading) {
    return (
      <Card className="production-history" style={workspaceStyle}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-md mb-md">
          <Title level={3} className="!mb-0 text-xl md:text-2xl">
            {productionTerminology.historyTitleLong}
          </Title>
        </div>

        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <Empty
            image={
              <ExclamationCircleOutlined
                style={{ fontSize: 48, color: "var(--ph-accent)" }}
              />
            }
            description={
              <div>
                <h3
                  style={{ fontSize: 18, fontWeight: "bold", marginBottom: 8 }}
                >
                  Aucun établissement sélectionné
                </h3>
                <p style={{ color: "var(--ph-muted)", marginBottom: 16 }}>
                  Veuillez sélectionner une entreprise et un établissement dans la barre de navigation
                  pour consulter l'historique de {productionTerminology.costContext}.
                </p>
                <Alert
                  message="Information importante"
                  description={`L'historique de ${productionTerminology.costContext} est filtré par établissement. Chaque établissement a son propre historique et ses ${productionTerminology.unitPlural} associées.`}
                  type="info"
                  showIcon
                  style={{ textAlign: "left", maxWidth: 500, margin: "0 auto" }}
                />
              </div>
            }
          />
        </div>
      </Card>
    );
  }

  // Colonnes du tableau

  const costColumns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      responsive: ["sm"],
    },
    {
      title: productionTerminology.unitSingularTitle,
      dataIndex: "production_unit_name",
      key: "production_unit_name",
    },
    {
      title: "Type",
      dataIndex: "production_kind",
      key: "production_kind",
      align: "center",
      width: 120,
      render: (kind) => (
        <Tag color={getProductionKindColor(kind)}>
          {getProductionKindLabel(kind)}
        </Tag>
      ),
      responsive: ["md"],
    },
    {
      title: "Code lot",
      dataIndex: "production_code",
      key: "production_code",
      width: 190,
      render: (value) => value || <Text type="secondary">N/A</Text>,
      responsive: ["sm"],
    },
    {
      title: "Qté produite",
      dataIndex: "output_quantity",
      key: "output_quantity",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
      width: 120,
      responsive: ["sm"],
    },
    {
      title: "Coût matières",
      dataIndex: "materials_cost_total",
      key: "materials_cost_total",
      align: "right",
      render: (value) => formatCurrency(value),
      width: 140,
      responsive: ["md"],
    },
    {
      title: "Coût variable",
      dataIndex: "variable_cost_total",
      key: "variable_cost_total",
      align: "right",
      render: (value) => formatCurrency(value),
      width: 140,
      responsive: ["md"],
    },
    {
      title: "Coût total",
      dataIndex: "total_cost",
      key: "total_cost",
      align: "right",
      render: (value) => formatCurrency(value),
      width: 140,
      responsive: ["md"],
    },
    {
      title: "Coût unitaire réel",
      dataIndex: "unit_cost_real",
      key: "unit_cost_real",
      align: "right",
      render: (value) => formatCurrency(value),
      width: 150,
      responsive: ["lg"],
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 100,
      render: (status) => {
        const normalized = String(status || "").toLowerCase();
        if (normalized === "deleted" || normalized === "cancelled") {
          return <Tag>SUPPRIMÉE</Tag>;
        }
        if (normalized === "failed") {
          return <Tag color="red">FAILED</Tag>;
        }
        return <Tag color="green">{String(status || "N/A").toUpperCase()}</Tag>;
      },
    },
  ];

  const costByUnitColumns = [
    {
      title: "Unité",
      dataIndex: "production_unit_name",
      key: "production_unit_name",
    },
    {
      title: "Type",
      dataIndex: "production_kind",
      key: "production_kind",
      align: "center",
      width: 120,
      render: (kind) => (
        <Tag color={getProductionKindColor(kind)}>
          {getProductionKindLabel(kind)}
        </Tag>
      ),
    },
    {
      title: "Productions",
      dataIndex: "production_count",
      key: "production_count",
      align: "right",
      width: 120,
    },
    {
      title: "Qté produite",
      dataIndex: "total_output_quantity",
      key: "total_output_quantity",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
      width: 140,
    },
    {
      title: "Coût total",
      dataIndex: "total_cost",
      key: "total_cost",
      align: "right",
      render: (value) => formatCurrency(value),
      width: 140,
    },
    {
      title: "Coût unitaire moyen",
      dataIndex: "avg_unit_cost_real",
      key: "avg_unit_cost_real",
      align: "right",
      render: (value) => formatCurrency(value),
      width: 170,
    },
  ];

  const costMonthlyTrendColumns = [
    {
      title: "Période",
      dataIndex: "period",
      key: "period",
      width: 120,
    },
    {
      title: "Qté produite",
      dataIndex: "total_output_quantity",
      key: "total_output_quantity",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
      width: 140,
    },
    {
      title: "Coût total",
      dataIndex: "total_cost",
      key: "total_cost",
      align: "right",
      render: (value) => formatCurrency(value),
      width: 140,
    },
    {
      title: "Coût unitaire moyen",
      dataIndex: "avg_unit_cost_real",
      key: "avg_unit_cost_real",
      align: "right",
      render: (value) => formatCurrency(value),
      width: 170,
    },
  ];

  return (
    <>
      <section className="production-history" style={workspaceStyle}>
        <header className="production-history__header">
          <div className="production-history__heading">
            <div className="production-history__title-line">
              <h1>{productionTerminology.historyTitleLong}</h1>
              {selectedWarehouseName && <Tag icon={<ShopOutlined />} color="blue">{selectedWarehouseName}</Tag>}
            </div>
            <p>Retrouvez une {productionTerminology.costContext === "cuisine" ? "préparation" : "production"} et consultez son détail.</p>
          </div>
          <div className="production-history__filters">
            <div className="production-history__period">
              <label htmlFor="production-history-dates">Période</label>
              <RangePicker id="production-history-dates" onChange={handleDateChange}
                placeholder={["Date de début", "Date de fin"]} allowClear value={dateRange} disabled={!selectedWarehouse} />
            </div>
            <Button icon={<ReloadOutlined aria-hidden="true" />} onClick={() => {
              const refresh = activeView === "history" ? loadHistory : loadCostTracking;
              void refresh().catch(() => {});
            }} disabled={!selectedWarehouse} loading={activeView === "history" ? loading : costLoading}>Actualiser</Button>
          </div>
        </header>
        <Tabs activeKey={activeView} onChange={setActiveView} destroyInactiveTabPane items={[
          { key: "history", label: productionTerminology.historyTitleLong, children: (
            <ProductionHistoryWorkspace
              key={JSON.stringify([contextKey, pagination.current, pagination.pageSize, startDate, endDate])}
              logs={logs} total={total} pagination={pagination} loading={loading}
              resource={historyResource} scope={businessScope} terminology={productionTerminology}
              canViewDetails={hasPermission("Gestion Commerciale.Approvisionnement.Production.Historique.details")}
              canCancel={hasPermission("Gestion Commerciale.Approvisionnement.Production.Historique.delete")}
              onPageChange={handleTableChange} onOpenDetails={handleViewDetails} onCancelProduction={handleOpenDeleteModal} />
          ) },
          { key: "costs", label: productionTerminology.costTrackingTitle, children: (
            <>
              <BusinessResourceNotice resource={productionResourceNotice(costsResource)} label="le suivi des coûts" />
              {costsResource.data && costsResource.data.freshness?.status !== "unavailable" && <>
                            {costSummary ? (
              <Descriptions bordered size="small" column={3}>
                <Descriptions.Item label="Qte totale produite">
                  {Number(costSummary.total_output_quantity || 0).toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Cout total matieres">
                  {formatCurrency(costSummary.total_material_cost)}
                </Descriptions.Item>
                <Descriptions.Item label="Cout variable total">
                  {formatCurrency(costSummary.total_variable_cost)}
                </Descriptions.Item>
                <Descriptions.Item label={productionTerminology.costTotalLabel}>
                  {formatCurrency(costSummary.total_cost)}
                </Descriptions.Item>
                <Descriptions.Item label="Cout unitaire moyen reel">
                  {formatCurrency(costSummary.avg_unit_cost_real)}
                </Descriptions.Item>
                <Descriptions.Item label="Nb logs">
                  {costTotal}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Alert
                type="info"
                showIcon
                message="Aucune donnee de cout pour cette periode."
              />
            )}

            <div className="overflow-x-auto" style={{ marginTop: 16 }}>
              <Table
                columns={costColumns}
                dataSource={costLogs}
                rowKey="id"
                scroll={{ x: "max-content" }}
                pagination={{
                  current: costPagination.current,
                  pageSize: costPagination.pageSize,
                  total: costTotal,
                  showSizeChanger: true,
                  showTotal: (currentTotal) => `Total ${currentTotal} logs couts`,
                }}
                loading={costLoading}
                onChange={handleCostTableChange}
                locale={{
                  emptyText: selectedWarehouse ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Aucun log de cout trouve pour cet établissement"
                    />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Selectionnez un établissement"
                    />
                  ),
                }}
              />
            </div>

            <Divider style={{ margin: "16px 0" }} />
            <Title level={5}>{productionTerminology.costByUnitTitle}</Title>
            <div className="overflow-x-auto">
              <Table
                columns={costByUnitColumns}
                dataSource={costByUnit}
                rowKey="production_unit_id"
                pagination={false}
                size="small"
                locale={{
                  emptyText: "Aucune agregation par unite disponible.",
                }}
              />
            </div>

            <Divider style={{ margin: "16px 0" }} />
            <Title level={5}>Tendance mensuelle</Title>
            <div className="overflow-x-auto">
              <Table
                columns={costMonthlyTrendColumns}
                dataSource={costMonthlyTrend}
                rowKey="period"
                pagination={false}
                size="small"
                locale={{
                  emptyText: "Aucune tendance mensuelle disponible.",
                }}
              />
            </div>
              </>}
            </>
          ) },
        ]} />
      </section>

      <ProductionDetailsModal
        open={detailsModalVisible} onClose={handleCloseDetailsModal}
        resource={detailsResource} productionTerminology={productionTerminology}
        formatCurrency={formatCurrency}
        canDeclareLotLoss={canDeclareLotLoss}
        canRegularizeVariableCost={canRegularizeVariableCost}
        canPayVariableCostExpense={canPayVariableCostExpense}
        handleOpenLotLossModal={handleOpenLotLossModal}
        handleOpenVariableCostRegularization={handleOpenVariableCostRegularization}
        handleOpenExpensePayment={handleOpenExpensePayment}
        getLotStatusColor={getLotStatusColor} getLotStatusLabel={getLotStatusLabel}
        getLossTypeLabel={getLossTypeLabel}
      />

      <Modal
        title="Declarer une perte sur lot"
        open={lotLossModalVisible}
        onCancel={() => handleCloseLotLossModal()}
        onOk={() => lotLossForm.submit()}
        confirmLoading={lotLossSubmitting}
        okText="Enregistrer"
        cancelText="Annuler"
        destroyOnClose
      >
        {selectedLotForLoss && (
          <>
            <Descriptions
              bordered
              column={1}
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Lot">
                <Tag color="purple">{selectedLotForLoss.lot_code}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Produit">
                {selectedLotForLoss.product_name}
              </Descriptions.Item>
              <Descriptions.Item label="Disponible">
                {selectedLotForLoss.quantity_available}{" "}
                {selectedLotForLoss.unit_short_name ||
                  selectedLotForLoss.unit_name ||
                  ""}
              </Descriptions.Item>
            </Descriptions>

            <Form
              form={lotLossForm}
              layout="vertical"
              onFinish={handleSubmitLotLoss}
            >
              <Form.Item
                name="quantity"
                label="Quantite perdue"
                rules={[
                  {
                    required: true,
                    message: "Veuillez saisir une quantite",
                  },
                ]}
              >
                <InputNumber
                  min={0.01}
                  max={Number(selectedLotForLoss.quantity_available || 0)}
                  step={0.01}
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item
                name="loss_type"
                label="Motif"
                rules={[
                  { required: true, message: "Veuillez selectionner un motif" },
                ]}
              >
                <Select
                  options={PRODUCTION_LOSS_TYPE_OPTIONS}
                  placeholder="Selectionner un motif"
                />
              </Form.Item>

              <Form.Item
                name="notes"
                label="Notes"
                rules={[
                  {
                    max: 500,
                    message: "Les notes ne doivent pas depasser 500 caracteres",
                  },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Precisez le contexte de la perte"
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {selectedVariableCost && selectedProductionDetails && (
        <ProductionExpenseRegularizationModal
          key={selectedVariableCost.id}
          cost={selectedVariableCost}
          details={selectedProductionDetails}
          onClose={() => setSelectedVariableCost(null)}
          onSuccess={async () => {
            await invalidateBusinessResources(queryClient, businessScope, "production");
            await loadProductionDetails(selectedProductionDetails.production.id);
          }}
        />
      )}

      {/* Modal pour annuler une production */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
            <span>Annuler une production</span>
            {logToDelete && <Tag color="red">ID: {logToDelete.id}</Tag>}
          </div>
        }
        open={deleteModalVisible}
        onCancel={handleCloseDeleteModal}
        footer={null}
        width={650}
        maskClosable={!deleteLoading}
        closable={!deleteLoading}
      >
        {logToDelete && (
          <div>
            <Alert
              message="Attention : impact sur le stock"
              description={
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <div>L'annulation va :</div>
                    <ul style={{ margin: "8px 0", paddingLeft: 18 }}>
                      <li>retirer le stock du (des) produit(s) fini(s)</li>
                      <li>
                        restaurer les quantités des {productionTerminology.materialPlural}
                      </li>
                      <li>créer des mouvements de stock pour la traçabilité</li>
                    </ul>
                  </div>
                  <p style={{ marginBottom: 0 }}>
                    Si le produit fini a déjà été vendu/transféré, l'annulation
                    sera bloquée pour éviter un stock négatif.
                  </p>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Date">
                {logToDelete.created_at}
              </Descriptions.Item>
              <Descriptions.Item label={productionTerminology.unitSingularTitle}>
                {logToDelete.production_unit_name}
              </Descriptions.Item>
              <Descriptions.Item label="Type de produit">
                <Tag color={getProductionKindColor(logToDelete.production_kind)}>
                  {getProductionKindLabel(logToDelete.production_kind)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Code lot">
                {logToDelete.production_code ? (
                  <Tag color="purple">{logToDelete.production_code}</Tag>
                ) : (
                  <Text type="secondary">N/A</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Quantité produite">
                {parseFloat(logToDelete.output_quantity).toFixed(2)}
              </Descriptions.Item>
            </Descriptions>

            <Form
              form={deleteForm}
              layout="vertical"
              onFinish={confirmDeleteProduction}
              disabled={deleteLoading}
            >
              <Form.Item
                label="Raison de l'annulation"
                name="reason"
                rules={[
                  { required: true, message: "Veuillez indiquer la raison" },
                  { min: 10, message: "Minimum 10 caractères" },
                ]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Ex: erreur de saisie, production doublonnée, correction..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item
                label={
                  <div>
                    Pour confirmer, tapez <Text code>SUPPRIMER</Text> :
                  </div>
                }
                name="confirm"
                rules={[
                  { required: true, message: 'Veuillez taper "SUPPRIMER"' },
                  {
                    validator: (_, value) => {
                      if (value === "SUPPRIMER") return Promise.resolve();
                      return Promise.reject(
                        new Error('Vous devez taper exactement "SUPPRIMER"')
                      );
                    },
                  },
                ]}
              >
                <Input placeholder="Tapez SUPPRIMER" autoComplete="off" />
              </Form.Item>

              <div style={{ textAlign: "right" }}>
                <Space>
                  <Button onClick={handleCloseDeleteModal} disabled={deleteLoading}>
                    Retour
                  </Button>
                  <Button
                    type="primary"
                    danger
                    htmlType="submit"
                    loading={deleteLoading}
                  >
                    Annuler la production
                  </Button>
                </Space>
              </div>
            </Form>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ProductionHistory;
